import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAccessToken } from "../store/auth";
import { apiFetch } from "../common/api";
import toast, { Toaster } from "react-hot-toast";

const HOST = import.meta.env.VITE_API_HOST;

/**
 * Transaction review page for Bitrix24-driven cancellation.
 * Super-users see details + action buttons; others see 403.
 */
export default function TransactionReview() {
  const { id } = useParams();
  const accessToken = useSelector(selectAccessToken);
  const navigate = useNavigate();
  const location = useLocation();

  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [comment, setComment] = useState("");

  const loadTransaction = useCallback(async () => {
    try {
      const resp = await apiFetch(`${HOST}/api/v1/review/transaction/${id}`);
      if (resp.status === 403) {
        setError(
          "Доступ запрещён. Только суперпользователи могут просматривать транзакции.",
        );
        return;
      }
      if (resp.status === 404) {
        setError("Транзакция не найдена.");
        return;
      }
      if (!resp.ok) {
        setError("Ошибка загрузки данных.");
        return;
      }
      const data = await resp.json();
      setTx(data);
      setError(null);
    } catch {
      setError("Ошибка сети.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!accessToken) {
      // Keep the review link across the login round-trip: without it the
      // operator lands on the dashboard and the cancellation is lost (#629).
      const target = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?redirect_to=${target}`, { replace: true });
      return;
    }

    loadTransaction();
  }, [
    accessToken,
    loadTransaction,
    location.pathname,
    location.search,
    navigate,
  ]);

  const handleCancel = async () => {
    const trimmed = comment.trim();
    if (!trimmed) {
      toast.error("Комментарий обязателен");
      return;
    }

    setCancelling(true);
    try {
      const resp = await apiFetch(
        `${HOST}/api/v1/review/transaction/${id}/cancel`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ comment: trimmed }),
        },
      );
      const data = await resp.json();
      if (resp.ok) {
        toast.success(data.message || "Транзакция отменена");
        setShowConfirm(false);
        setComment("");
        await loadTransaction();
      } else {
        toast.error(data.detail || "Ошибка отмены");
      }
    } catch {
      toast.error("Ошибка сети");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-lg mb-4">{error}</div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Toaster position="top-center" />
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">
              Отмена транзакции #{tx.transactionId}
            </h1>
            <span
              className={`inline-block mt-2 px-2 py-1 text-xs font-medium rounded ${
                tx.status === "Succeed"
                  ? "bg-green-100 text-green-800"
                  : tx.status === "RolledBack"
                    ? "bg-red-100 text-red-800"
                    : "bg-gray-100 text-gray-800"
              }`}
            >
              {tx.status === "Succeed"
                ? "Активна"
                : tx.status === "RolledBack"
                  ? "Отменена"
                  : tx.status}
            </span>
          </div>

          <div className="px-6 py-4 space-y-3">
            <DetailRow label="Тип транзакции" value={tx.reasonLabel} />
            <DetailRow label="Локация" value={tx.locationName} />
            <DetailRow label="Администратор" value={tx.adminName} />
            <DetailRow label="Клиент" value={tx.customerName} />
            <DetailRow
              label="Телефон"
              value={
                tx.customerPhone
                  ? `****${tx.customerPhone.slice(-4)}`
                  : "Не указан"
              }
            />
            <DetailRow
              label="Минуты"
              value={`${tx.minutesAmount > 0 ? "+" : ""}${tx.minutesAmount} мин.`}
            />
            <DetailRow
              label="Стоимость"
              value={tx.priceRub ? `${tx.priceRub.toFixed(2)} руб.` : "—"}
            />
            <DetailRow
              label="Баланс"
              value={`${tx.balance} мин.${
                tx.debt > 0 ? ` (долг ${tx.debt} мин.)` : ""
              }`}
            />
            <DetailRow
              label="Комментарий менеджера"
              value={tx.managerComment}
            />
            <DetailRow
              label="Комментарий администратора"
              value={tx.cancellationReason}
            />
            <DetailRow label="Дата" value={formatDate(tx.createdAt)} />
          </div>

          {tx.status === "Succeed" && (
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowConfirm(true)}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Отменить транзакцию
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Подтвердите отмену
              </h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 mb-4">
                Вы уверены, что хотите отменить транзакцию #{tx.transactionId}?
                Это действие нельзя отменить.
              </p>
              <div className="mb-4">
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="cancel-comment"
                >
                  Комментарий (обязательно)
                </label>
                <textarea
                  id="cancel-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Почему отменяем транзакцию?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={cancelling}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling || !comment.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancelling ? "Выполняется..." : "Да, отменить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium text-right">{value}</span>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
